import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { 
  Save, 
  History, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  ChevronDown, 
  ChevronRight,
  FileText,
  Calculator,
  Lock,
  Unlock,
  Filter
} from 'lucide-react';

const ALUMNO_STATUSES = [
  { value: 'activo',       label: 'Activo',        color: 'bg-green-100 text-green-800' },
  { value: 'nuevo',        label: 'Nuevo',          color: 'bg-blue-100 text-blue-800' },
  { value: 'aceptado',     label: 'Aceptado',       color: 'bg-teal-100 text-teal-800' },
  { value: 'aspirante',    label: 'Aspirante',      color: 'bg-purple-100 text-purple-800' },
  { value: 'deudor',       label: 'Deudor',         color: 'bg-orange-100 text-orange-700' },
  { value: 'baja_administrativa', label: 'Baja Administrativa', color: 'bg-red-100 text-red-800' },
  { value: 'baja',         label: 'Baja',           color: 'bg-red-100 text-red-800' },
  { value: 'baja_temporal',label: 'Baja Temporal',  color: 'bg-orange-100 text-orange-800' },
  { value: 'egresado',     label: 'Egresado',       color: 'bg-gray-100 text-gray-800' },
  { value: 'suspendido',   label: 'Suspendido',     color: 'bg-yellow-100 text-yellow-800' },
];

// Estatus que Configuración General > Estatus Alumnos puede desbloquear (ver
// backend/src/utils/alumnos.ts PAYMENT_RELATED_BLOCKED_STATUSES). Cuando el filtro
// de adeudo está desactivado, se agregan automáticamente al filtro por defecto.
const PAYMENT_RELATED_BLOCKED_STATUSES = ['deudor', 'baja_administrativa', 'aspirante'];
const BASE_DEFAULT_STATUSES = ['activo', 'nuevo', 'aceptado'];

interface AcademicPeriod {
  id: number;
  code: string;
  name: string;
  type: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  programId: number | null;
}

interface EvaluationCategory {
  name: string;
  weightPercent: number;
  minimumPassingScore?: number | null;
}

interface ProgramCourse {
  courseId: number;
  courseName: string;
  courseCode: string;
  evaluationPlanCategories?: EvaluationCategory[] | null;
}

interface GradebookEntry {
  slotId: number;
  entryId: number | null;
  rawScore: string | null;
  percentage: string | null;
  status: string;
  gradedAt: string | null;
}

interface StudentRow {
  student: {
    id: number;
    nui: string;
    fullName: string;
    estatus: string;
  };
  entries: GradebookEntry[];
  categoryAverages: Record<number, number>;
  finalAverage: number;
  finalGrade: number;
  isNoAprobado: boolean;
  riskLevel: 'red' | 'yellow' | 'green';
}

interface Slot {
  id: number;
  categoryId: number;
  categoryName: string;
  categoryWeight: string;
  title: string;
  maxScore: string;
  weight: string;
  dueDate: string | null;
  sequence: number;
}

interface Category {
  id: number;
  name: string;
  weightPercent: string;
}

interface GradebookData {
  plan: {
    id: number;
    title: string;
    periodName: string;
    courseName: string;
    status: string;
  };
  categories: Category[];
  slots: Slot[];
  matrix: StudentRow[];
  isLocked: boolean;
}

const periodTypeLabels: Record<string, string> = {
  matutino: 'Matutino',
  vespertino: 'Vespertino',
  sabatino: 'Sabatino',
};

const formatDateToDDMMYYYY = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'Sin fecha';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function Gradebook() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const queryPeriodId = searchParams.get('periodId');
  const queryCourseId = searchParams.get('courseId');
  const initialPeriodId = queryPeriodId ? Number(queryPeriodId) : null;
  const initialCourseId = queryCourseId ? Number(queryCourseId) : null;

  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(initialPeriodId);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(initialCourseId);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [editedGrades, setEditedGrades] = useState<Record<string, string>>({});
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(BASE_DEFAULT_STATUSES);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  
  const [periodSearchTerm, setPeriodSearchTerm] = useState('');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);

  const [showConceptoDialog, setShowConceptoDialog] = useState(false);
  const [conceptoText, setConceptoText] = useState('');
  const [pendingSave, setPendingSave] = useState<
    | { type: 'single'; alumnoId: number; slotId: number; rawScore: string }
    | { type: 'bulk'; entries: Array<{ slotId: number; alumnoId: number; rawScore: string }> }
    | null
  >(null);

  const { data: periods, isLoading: loadingPeriods } = useQuery<AcademicPeriod[]>({
    queryKey: ['academic-periods'],
    queryFn: async () => {
      const response = await axios.get('/api/academic-periods');
      return response.data;
    }
  });

  const selectedPeriodData = periods?.find(p => p.id === selectedPeriodId);

  const { data: programCourses, isLoading: loadingCourses } = useQuery<ProgramCourse[]>({
    queryKey: ['program-courses-for-gradebook', selectedPeriodData?.programId],
    queryFn: async () => {
      const response = await axios.get(`/api/academic-programs/${selectedPeriodData?.programId}/courses-with-details`);
      return response.data;
    },
    enabled: !!selectedPeriodData?.programId
  });

  const { data: plans, isLoading: loadingPlans } = useQuery({
    queryKey: ['evaluation-plans-by-period-course', selectedPeriodId, selectedCourseId],
    queryFn: async () => {
      const response = await axios.get(`/api/evaluation/plans?periodId=${selectedPeriodId}&courseId=${selectedCourseId}`);
      return response.data;
    },
    enabled: !!selectedPeriodId && !!selectedCourseId
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.length > 1 ? prev.filter(s => s !== status) : prev // al menos 1 siempre seleccionado
        : [...prev, status]
    );
  };

  // Configuración General > Estatus Alumnos: si el tenant desactivó el filtro de
  // adeudo, los alumnos deudor/baja_administrativa/aspirante deben verse por
  // defecto, sin que el profesor tenga que marcarlos manualmente en el filtro.
  const { data: debtStatusSettings } = useQuery({
    queryKey: ['debt-status-filter-settings'],
    queryFn: async () => {
      const response = await axios.get('/api/alumnos/status-settings');
      return response.data as { debtStatusFilterEnabled: boolean };
    },
  });

  const appliedDebtFilterDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDebtFilterDefaultRef.current || !debtStatusSettings) return;
    appliedDebtFilterDefaultRef.current = true;
    if (debtStatusSettings.debtStatusFilterEnabled === false) {
      setSelectedStatuses(prev => Array.from(new Set([...prev, ...PAYMENT_RELATED_BLOCKED_STATUSES])));
    }
  }, [debtStatusSettings]);

  const { data: gradebookData, isLoading } = useQuery<GradebookData>({
    queryKey: ['gradebook-matrix', selectedPlanId, selectedStatuses.sort().join(',')],
    queryFn: async () => {
      const statusParam = selectedStatuses.join(',');
      const response = await axios.get(`/api/gradebook/plan/${selectedPlanId}/matrix?statuses=${statusParam}`);
      return response.data;
    },
    enabled: !!selectedPlanId
  });

  const { data: auditHistory } = useQuery({
    queryKey: ['grade-audit', selectedEntryId],
    queryFn: async () => {
      const response = await axios.get(`/api/gradebook/entries/${selectedEntryId}/history`);
      return response.data;
    },
    enabled: !!selectedEntryId && historyDialogOpen
  });

  const saveGradeMutation = useMutation({
    mutationFn: async (data: { slotId: number; alumnoId: number; rawScore: string; reasonText: string }) => {
      const response = await axios.post('/api/gradebook/entries', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradebook-matrix', selectedPlanId] });
      toast({ title: 'Calificación guardada', variant: 'default' });
    },
    onError: () => {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  });

  const bulkSaveMutation = useMutation({
    mutationFn: async ({ entries, reasonText }: { entries: Array<{ slotId: number; alumnoId: number; rawScore: string }>; reasonText: string }) => {
      const response = await axios.post('/api/gradebook/entries/bulk', { entries, reasonText });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['gradebook-matrix', selectedPlanId] });
      setEditedGrades({});
      toast({ title: `${data.count} calificaciones guardadas`, variant: 'default' });
    },
    onError: () => {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  });

  const closeSlotGradesMutation = useMutation({
    mutationFn: async (slotId: number) => {
      const response = await axios.post(`/api/gradebook/slots/${slotId}/close`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['gradebook-matrix', selectedPlanId] });
      toast({ title: data.message, variant: 'default' });
    },
    onError: () => {
      toast({ title: 'Error al cerrar calificaciones', variant: 'destructive' });
    }
  });

  // Tracks the previous periodId (rather than a one-shot "skip" flag) so the reset only
  // fires on an actual change. A one-shot flag gets consumed by React StrictMode's
  // synthetic double-invocation of effects in dev, wiping out the courseId seeded from
  // the query string before the user ever touched the period selector.
  const prevPeriodIdRef = useRef(selectedPeriodId);
  useEffect(() => {
    if (prevPeriodIdRef.current === selectedPeriodId) {
      return;
    }
    prevPeriodIdRef.current = selectedPeriodId;
    if (selectedPeriodId) {
      setSelectedCourseId(null);
      setSelectedPlanId(null);
      setEditedGrades({});
    }
  }, [selectedPeriodId]);

  useEffect(() => {
    if (selectedCourseId) {
      setSelectedPlanId(null);
      setEditedGrades({});
    }
  }, [selectedCourseId]);

  const validateCategories = (categories: EvaluationCategory[] | null | undefined): boolean => {
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
      return false;
    }
    const totalWeight = categories.reduce((sum, cat) => sum + (cat.weightPercent || 0), 0);
    return Math.abs(totalWeight - 100) <= 0.01;
  };

  useEffect(() => {
    if (plans && plans.length > 0 && selectedCourseId) {
      setSelectedPlanId(plans[0].id);
    } else if (plans && plans.length === 0 && selectedCourseId) {
      const selectedCourse = programCourses?.find(c => c.courseId === selectedCourseId);
      if (selectedCourse && validateCategories(selectedCourse.evaluationPlanCategories)) {
        createPlanFromCourse(selectedCourse);
      }
    }
  }, [plans, selectedCourseId]);

  const createPlanFromCourse = async (course: ProgramCourse) => {
    if (!course.evaluationPlanCategories || !selectedPeriodId) return;
    
    if (!validateCategories(course.evaluationPlanCategories)) {
      toast({ 
        title: 'Plan de evaluación inválido', 
        description: 'Las categorías de la materia no suman 100%',
        variant: 'destructive' 
      });
      return;
    }
    
    try {
      setIsCreatingPlan(true);
      
      const response = await axios.post('/api/evaluation/plans/from-course', {
        periodId: selectedPeriodId,
        courseId: course.courseId,
        title: `Plan de ${course.courseName}`,
        categories: course.evaluationPlanCategories
      });
      
      queryClient.invalidateQueries({ queryKey: ['evaluation-plans-by-period-course', selectedPeriodId, selectedCourseId] });
      setSelectedPlanId(response.data.id);
      toast({ title: 'Plan de evaluación creado automáticamente', variant: 'default' });
    } catch (error) {
      console.error('Error creating plan:', error);
      toast({ title: 'Error al crear plan de evaluación', variant: 'destructive' });
    } finally {
      setIsCreatingPlan(false);
    }
  };

  useEffect(() => {
    if (gradebookData?.categories) {
      setExpandedCategories(new Set(gradebookData.categories.map(c => c.id)));
    }
  }, [gradebookData?.categories]);

  const handleGradeChange = (alumnoId: number, slotId: number, value: string) => {
    const key = `${alumnoId}-${slotId}`;
    setEditedGrades(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveAll = () => {
    const entries = Object.entries(editedGrades).map(([key, rawScore]) => {
      const [alumnoId, slotId] = key.split('-').map(Number);
      return { alumnoId, slotId, rawScore };
    });
    if (entries.length === 0) return;
    setPendingSave({ type: 'bulk', entries });
    setConceptoText('');
    setShowConceptoDialog(true);
  };

  const handleSingleSave = (alumnoId: number, slotId: number, rawScore: string) => {
    setPendingSave({ type: 'single', alumnoId, slotId, rawScore });
    setConceptoText('');
    setShowConceptoDialog(true);
  };

  const confirmConceptoAndSave = () => {
    if (!conceptoText.trim() || !pendingSave) return;
    if (pendingSave.type === 'single') {
      saveGradeMutation.mutate({ ...pendingSave, reasonText: conceptoText.trim() });
    } else {
      bulkSaveMutation.mutate({ entries: pendingSave.entries, reasonText: conceptoText.trim() });
    }
    setShowConceptoDialog(false);
    setConceptoText('');
    setPendingSave(null);
  };

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const getRiskBadge = (level: string) => {
    switch (level) {
      case 'red':
        return <Badge variant="destructive" className="ml-2"><AlertTriangle className="w-3 h-3 mr-1" />En riesgo</Badge>;
      case 'yellow':
        return <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Atención</Badge>;
      case 'green':
        return <Badge variant="default" className="ml-2 bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Bien</Badge>;
      case 'none':
      default:
        return null; // No badge when no locked grades
    }
  };

  const getCellBackground = (entry: GradebookEntry, maxScore: string) => {
    if (!entry.rawScore) return 'bg-gray-50';
    const pct = (parseFloat(entry.rawScore) / parseFloat(maxScore)) * 100;
    if (pct < 60) return 'bg-red-50';
    if (pct < 70) return 'bg-yellow-50';
    return 'bg-green-50';
  };

  const groupedSlots = gradebookData?.categories.map(cat => ({
    ...cat,
    slots: gradebookData.slots.filter(s => s.categoryId === cat.id)
  })) || [];

  const hasChanges = Object.keys(editedGrades).length > 0;

  const selectedCourseData = programCourses?.find(c => c.courseId === selectedCourseId);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Libro de Calificaciones</h1>
        <p className="text-gray-500">Registro y seguimiento de calificaciones por plan de evaluación</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              1. Seleccionar Grupo Académico
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={loadingPeriods ? "Cargando grupos..." : "Buscar por clave o nombre..."}
                value={isPeriodDropdownOpen ? periodSearchTerm : (selectedPeriodData ? `${selectedPeriodData.code} - ${selectedPeriodData.name}` : '')}
                onChange={(e) => {
                  setPeriodSearchTerm(e.target.value);
                  if (!isPeriodDropdownOpen) setIsPeriodDropdownOpen(true);
                }}
                onFocus={() => {
                  setIsPeriodDropdownOpen(true);
                  if (selectedPeriodId) {
                    setPeriodSearchTerm('');
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setIsPeriodDropdownOpen(false), 200);
                }}
                disabled={loadingPeriods}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                🔍
              </span>
            </div>
            
            {isPeriodDropdownOpen && !loadingPeriods && periods && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {periods.filter(period => {
                  const searchLower = periodSearchTerm.toLowerCase();
                  return (
                    period.name.toLowerCase().includes(searchLower) ||
                    period.code.toLowerCase().includes(searchLower) ||
                    (period.type && period.type.toLowerCase().includes(searchLower))
                  );
                }).length > 0 ? (
                  periods.filter(period => {
                    const searchLower = periodSearchTerm.toLowerCase();
                    return (
                      period.name.toLowerCase().includes(searchLower) ||
                      period.code.toLowerCase().includes(searchLower) ||
                      (period.type && period.type.toLowerCase().includes(searchLower))
                    );
                  }).map(period => (
                    <div
                      key={period.id}
                      className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedPeriodId === period.id ? 'bg-blue-100' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSelectedPeriodId(period.id);
                        setSelectedCourseId(null);
                        setSelectedPlanId(null);
                        setPeriodSearchTerm('');
                        setIsPeriodDropdownOpen(false);
                      }}
                    >
                      <div className="font-medium">{period.code} - {period.name}</div>
                      <div className="text-xs text-gray-500">
                        {periodTypeLabels[period.type] || period.type}
                        {period.startDate && ` | ${formatDateToDDMMYYYY(period.startDate)} - ${formatDateToDDMMYYYY(period.endDate)}`}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    No se encontraron grupos con "{periodSearchTerm}"
                  </div>
                )}
              </div>
            )}
            
            {selectedPeriodId && !isPeriodDropdownOpen && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPeriodId(null);
                  setSelectedCourseId(null);
                  setSelectedPlanId(null);
                  setPeriodSearchTerm('');
                }}
                className="absolute right-8 top-[38px] text-gray-400 hover:text-red-500"
                title="Limpiar selección"
              >
                ✕
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              2. Seleccionar Materia
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={selectedCourseId || ''}
              onChange={e => setSelectedCourseId(Number(e.target.value) || null)}
              disabled={!selectedPeriodId || loadingCourses || !selectedPeriodData?.programId}
            >
              <option value="">
                {!selectedPeriodId 
                  ? '-- Primero seleccione un grupo --' 
                  : !selectedPeriodData?.programId
                    ? '-- El grupo no tiene programa asignado --'
                    : '-- Seleccione una materia --'}
              </option>
              {programCourses?.map((course) => (
                <option key={course.courseId} value={course.courseId}>
                  {course.courseCode} - {course.courseName}
                  {validateCategories(course.evaluationPlanCategories) ? ' ✓' : course.evaluationPlanCategories ? ' ⚠️' : ''}
                </option>
              ))}
            </select>
            {loadingCourses && (
              <p className="text-sm text-gray-500 mt-1">Cargando materias...</p>
            )}
            {selectedPeriodId && !loadingCourses && (!programCourses || programCourses.length === 0) && selectedPeriodData?.programId && (
              <p className="text-sm text-yellow-600 mt-1">No hay materias en el programa</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              3. Plan de Evaluación
            </label>
            {isCreatingPlan ? (
              <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500">
                Creando plan automáticamente...
              </div>
            ) : selectedPlanId ? (
              <div className="w-full border border-green-300 rounded-lg px-3 py-2 bg-green-50 text-green-800">
                {plans?.find((p: any) => p.id === selectedPlanId)?.title || 'Plan activo'}
              </div>
            ) : selectedCourseId && validateCategories(selectedCourseData?.evaluationPlanCategories) ? (
              <div className="w-full border border-blue-300 rounded-lg px-3 py-2 bg-blue-50 text-blue-800">
                Se creará automáticamente...
              </div>
            ) : selectedCourseId && selectedCourseData?.evaluationPlanCategories ? (
              <div className="w-full border border-red-300 rounded-lg px-3 py-2 bg-red-50 text-red-800">
                Plan inválido (pesos no suman 100%)
              </div>
            ) : selectedCourseId ? (
              <div className="w-full border border-yellow-300 rounded-lg px-3 py-2 bg-yellow-50 text-yellow-800">
                Materia sin plan de evaluación
              </div>
            ) : (
              <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500">
                -- Seleccione una materia --
              </div>
            )}
          </div>

          <div className="relative" ref={statusDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              4. Filtrar por Estatus
            </label>
            <button
              type="button"
              onClick={() => setIsStatusDropdownOpen(prev => !prev)}
              className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm"
            >
              <span className="flex items-center gap-1 flex-wrap">
                <Filter className="w-4 h-4 text-gray-500 shrink-0" />
                <span className="text-gray-700">
                  {selectedStatuses.length === 1
                    ? ALUMNO_STATUSES.find(s => s.value === selectedStatuses[0])?.label
                    : `${selectedStatuses.length} estatus`}
                </span>
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isStatusDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                {ALUMNO_STATUSES.map(s => (
                  <label
                    key={s.value}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(s.value)}
                      onChange={() => toggleStatus(s.value)}
                      className="rounded"
                    />
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
                      {s.label}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-end">
            {hasChanges && (
              <Button onClick={handleSaveAll} disabled={bulkSaveMutation.isPending} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                Guardar cambios ({Object.keys(editedGrades).length})
              </Button>
            )}
          </div>
        </div>

        {selectedPeriodData && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
            <p className="text-sm font-medium text-gray-700">
              Grupo: {selectedPeriodData.name} - {periodTypeLabels[selectedPeriodData.type] || selectedPeriodData.type}
            </p>
            {selectedPeriodData.startDate && selectedPeriodData.endDate && (
              <p className="text-sm text-gray-600">
                Período: {selectedPeriodData.startDate} al {selectedPeriodData.endDate}
              </p>
            )}
            {selectedCourseData && (
              <p className="text-sm text-gray-600 mt-1">
                Materia: {selectedCourseData.courseCode} - {selectedCourseData.courseName}
              </p>
            )}
          </div>
        )}
      </div>

      {gradebookData?.plan && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {gradebookData.plan.title}
                {gradebookData.isLocked && (
                  <Badge variant="secondary" className="ml-2">Bloqueado</Badge>
                )}
              </CardTitle>
              <div className="text-sm text-gray-500">
                {gradebookData.plan.courseName} | {gradebookData.plan.periodName}
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : gradebookData.slots.length === 0 ? (
              <div className="text-center py-8 bg-yellow-50 rounded-lg border border-yellow-200">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <p className="text-lg font-medium text-yellow-800">No hay avances de evaluación configurados</p>
                <p className="text-sm text-yellow-600 mt-2">
                  Para registrar calificaciones, primero debes configurar los avances en el plan de evaluación.
                </p>
                <p className="text-sm text-yellow-600 mt-1">
                  Ve a <strong>Planes de Evaluación</strong> y haz clic en el ícono de engranaje (⚙️) en cada categoría para agregar avances.
                </p>
              </div>
            ) : gradebookData.matrix.length === 0 ? (
              <div className="text-center py-8 bg-blue-50 rounded-lg border border-blue-200">
                <FileText className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <p className="text-lg font-medium text-blue-800">No hay alumnos inscritos en este grupo</p>
                <p className="text-sm text-blue-600 mt-2">
                  Los alumnos deben estar asignados al grupo académico para poder registrar sus calificaciones.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left sticky left-0 bg-gray-100 z-10 min-w-[200px]">
                        Alumno
                      </th>
                      {groupedSlots.map(cat => (
                        <React.Fragment key={cat.id}>
                          {expandedCategories.has(cat.id) ? (
                            <>
                              <th
                                colSpan={cat.slots.length}
                                className="border p-2 text-center bg-blue-50 cursor-pointer hover:bg-blue-100"
                                onClick={() => toggleCategory(cat.id)}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <ChevronDown className="w-4 h-4" />
                                  {cat.name} ({cat.weightPercent}%)
                                </div>
                              </th>
                            </>
                          ) : (
                            <th
                              className="border p-2 text-center bg-blue-50 cursor-pointer hover:bg-blue-100"
                              onClick={() => toggleCategory(cat.id)}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <ChevronRight className="w-4 h-4" />
                                {cat.name}
                              </div>
                            </th>
                          )}
                        </React.Fragment>
                      ))}
                      <th className="border p-2 text-center bg-gray-200 min-w-[100px]">
                        <div className="flex items-center justify-center gap-1">
                          <Calculator className="w-4 h-4" />
                          Promedio
                        </div>
                      </th>
                    </tr>
                    <tr className="bg-gray-50">
                      <th className="border p-1 sticky left-0 bg-gray-50 z-10"></th>
                      {groupedSlots.map(cat => (
                        <React.Fragment key={cat.id}>
                          {expandedCategories.has(cat.id) ? (
                            cat.slots.map(slot => {
                              const slotEntries = gradebookData?.matrix.map(row => 
                                row.entries.find(e => e.slotId === slot.id)
                              ).filter(Boolean) || [];
                              const lockedCount = slotEntries.filter(e => e?.status === 'locked').length;
                              const gradedCount = slotEntries.filter(e => e?.status === 'graded').length;
                              const allLocked = lockedCount > 0 && gradedCount === 0;
                              const hasGradedToClose = gradedCount > 0;
                              
                              return (
                                <th key={slot.id} className="border p-1 text-center text-xs">
                                  <div className="font-medium flex items-center justify-center gap-1">
                                    {slot.title}
                                    {allLocked && <Lock className="w-3 h-3 text-green-600" />}
                                  </div>
                                  <div className="text-gray-400">/{slot.maxScore}</div>
                                  {hasGradedToClose && !gradebookData?.isLocked && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-5 px-1 text-xs mt-1"
                                      onClick={() => closeSlotGradesMutation.mutate(slot.id)}
                                      disabled={closeSlotGradesMutation.isPending}
                                    >
                                      <Lock className="w-3 h-3 mr-1" />
                                      Cerrar ({gradedCount})
                                    </Button>
                                  )}
                                  {lockedCount > 0 && (
                                    <div className="text-xs text-green-600 mt-1">
                                      {lockedCount} cerradas
                                    </div>
                                  )}
                                </th>
                              );
                            })
                          ) : (
                            <th className="border p-1 text-center text-xs text-gray-500">
                              {cat.slots.length} actividades
                            </th>
                          )}
                        </React.Fragment>
                      ))}
                      <th className="border p-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradebookData.matrix.map((row) => (
                      <tr key={row.student.id} className="hover:bg-gray-50">
                        <td className="border p-2 sticky left-0 bg-white z-10">
                          <div className="font-medium text-sm">{row.student.fullName}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">{row.student.nui}</span>
                            {(() => {
                              const s = ALUMNO_STATUSES.find(s => s.value === row.student.estatus);
                              return s ? (
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${s.color}`}>
                                  {s.label}
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </td>
                        {groupedSlots.map(cat => (
                          <React.Fragment key={cat.id}>
                            {expandedCategories.has(cat.id) ? (
                              cat.slots.map(slot => {
                                const entry = row.entries.find(e => e.slotId === slot.id);
                                const key = `${row.student.id}-${slot.id}`;
                                const currentValue = editedGrades[key] ?? entry?.rawScore ?? '';
                                
                                return (
                                  <td
                                    key={slot.id}
                                    className={`border p-1 text-center ${getCellBackground(entry!, slot.maxScore)}`}
                                  >
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="number"
                                        min="0"
                                        max={slot.maxScore}
                                        step="0.1"
                                        value={currentValue}
                                        onChange={(e) => handleGradeChange(row.student.id, slot.id, e.target.value)}
                                        onBlur={(e) => {
                                          if (e.target.value !== (entry?.rawScore ?? '')) {
                                            handleSingleSave(row.student.id, slot.id, e.target.value);
                                          }
                                        }}
                                        className="w-16 h-8 text-center text-sm p-1"
                                        disabled={gradebookData.isLocked}
                                      />
                                      {entry?.entryId && (
                                        <button
                                          onClick={() => {
                                            setSelectedEntryId(entry.entryId);
                                            setHistoryDialogOpen(true);
                                          }}
                                          className="text-gray-400 hover:text-gray-600"
                                          title="Ver historial"
                                        >
                                          <History className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                );
                              })
                            ) : (
                              <td className="border p-2 text-center bg-gray-50">
                                <span className="font-medium">
                                  {row.categoryAverages[cat.id]?.toFixed(1) || '-'}
                                </span>
                              </td>
                            )}
                          </React.Fragment>
                        ))}
                        <td className="border p-2 text-center font-bold">
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-1">
                              <span className={row.isNoAprobado ? 'text-red-700' : ''}>
                                {(row.finalGrade ?? row.finalAverage).toFixed(1)}
                              </span>
                              {getRiskBadge(row.riskLevel)}
                            </div>
                            {row.isNoAprobado && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
                                No Aprobado
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showConceptoDialog} onOpenChange={(open) => {
        if (!open) { setShowConceptoDialog(false); setPendingSave(null); setConceptoText(''); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Concepto del cambio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              {pendingSave?.type === 'bulk'
                ? `Se guardarán ${(pendingSave as any).entries?.length} calificación(es). Escribe el motivo o concepto del cambio.`
                : 'Escribe el motivo o concepto de este cambio de calificación.'}
            </p>
            <div className="space-y-1">
              <Label htmlFor="conceptoInput">Concepto <span className="text-red-500">*</span></Label>
              <Textarea
                id="conceptoInput"
                placeholder="Ej: Corrección de error de captura, Revisión de examen, Ajuste por actividad entregada..."
                value={conceptoText}
                onChange={(e) => setConceptoText(e.target.value)}
                rows={3}
                className="resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) confirmConceptoAndSave();
                }}
                autoFocus
              />
              {conceptoText.trim() === '' && (
                <p className="text-xs text-red-500">El concepto es obligatorio para guardar.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowConceptoDialog(false); setPendingSave(null); setConceptoText(''); }}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmConceptoAndSave}
              disabled={!conceptoText.trim() || saveGradeMutation.isPending || bulkSaveMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de cambios
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-3">
            {auditHistory?.map((log: any) => (
              <div key={log.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant={log.action === 'create' ? 'default' : 'secondary'}>
                    {log.action === 'create' ? 'Creado' : 'Modificado'}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {new Date(log.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">{log.actorName}</span>
                  {log.reasonText && (
                    <p className="text-gray-600 mt-1">{log.reasonText}</p>
                  )}
                </div>
                {log.action === 'update' && (
                  <div className="mt-2 text-xs">
                    <span className="text-red-600 line-through">
                      {log.previousValue?.rawScore}
                    </span>
                    <span className="mx-2">→</span>
                    <span className="text-green-600 font-medium">
                      {log.newValue?.rawScore}
                    </span>
                  </div>
                )}
              </div>
            ))}
            {(!auditHistory || auditHistory.length === 0) && (
              <p className="text-center text-gray-500 py-4">No hay historial disponible</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
